import React, { useEffect, useState, useCallback } from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getSessions,
  getGrades,
  getSubjectsByGrade,
  getStudentsByGrade,
  getAttendances,
  saveAttendance,
} from "../../api/attendanceApi";

import AttendanceForm from "./AttendanceForm";
import "../../styles/AttendancePage.scss";

const AttendancePage = () => {
  const [sessions, setSessions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [date, setDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [hasAttendance, setHasAttendance] = useState(false);

  // 🔹 Load Sessions
  useEffect(() => {
    getSessions()
      .then((res) => {
        const all = Array.isArray(res) ? res : res?.data || [];
        const active = all.filter(
          (s) => s.status?.toLowerCase() === "active" || s.is_active === true
        );
        setSessions(active);
      })
      .catch(() => toast.error("Failed to load sessions"));
  }, []);

  // 🔹 Load Grades
  useEffect(() => {
    if (!selectedSession) {
      setGrades([]);
      setSelectedGrade(null);
      return;
    }
    getGrades(selectedSession)
      .then((res) => setGrades(Array.isArray(res) ? res : res?.data || []))
      .catch(() => toast.error("Failed to load grades"));
  }, [selectedSession]);

  // 🔹 Load Subjects
  useEffect(() => {
    if (!selectedGrade) {
      setSubjects([]);
      setSelectedSubject(null);
      return;
    }
    getSubjectsByGrade(selectedGrade)
      .then((res) => setSubjects(Array.isArray(res) ? res : res?.data || []))
      .catch(() => toast.error("Failed to load subjects"));
  }, [selectedGrade]);

  // 🔹 Load Attendance
  const loadAttendance = useCallback(
    async (useDate = date) => {
      if (!selectedSession || !selectedGrade || !selectedSubject || !useDate) {
        setStudents([]);
        setHasAttendance(false);
        return;
      }

      try {
        const studentsData = await getStudentsByGrade(selectedGrade);
        const sd = Array.isArray(studentsData)
          ? studentsData
          : studentsData?.data || [];

        const attendanceData = await getAttendances({
          sessionId: selectedSession,
          gradeId: selectedGrade,
          subjectId: selectedSubject,
          date: useDate.toISOString().split("T")[0],
        });
        const ad = Array.isArray(attendanceData)
          ? attendanceData
          : attendanceData?.data || [];

        setHasAttendance(ad.length > 0);

        const merged = sd.map((s) => {
          const rec = ad.find((a) => {
            const attStudentId = a.student_id ?? a.studentId ?? a.student?.id;
            return +attStudentId === +s.id;
          });
          const status = rec?.status ? String(rec.status) : "Not Marked";
          return { ...s, status };
        });

        setStudents(merged);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load attendance data");
        setStudents([]);
        setHasAttendance(false);
      }
    },
    [selectedSession, selectedGrade, selectedSubject, date]
  );

  useEffect(() => {
    loadAttendance();
  }, [selectedSession, selectedGrade, selectedSubject, date, loadAttendance]);

  const handleStatusChange = (studentId, status) => {
    setStudents((prev) =>
      prev.map((s) => (s.id === studentId ? { ...s, status } : s))
    );
  };

  // 🔹 Save or Update Attendance
  const handleUpdateAttendance = async () => {
    if (!selectedSession || !selectedGrade || !selectedSubject) {
      toast.error("Select session, grade and subject first");
      return;
    }

    try {
      const payload = {
        sessionId: selectedSession,
        gradeId: selectedGrade,
        subjectId: selectedSubject,
        date: date.toISOString().split("T")[0],
        records: students.map((s) => ({ studentId: s.id, status: s.status })),
      };

      const res = await saveAttendance(payload);

      if (res.success) {
        if (hasAttendance) {
          toast.success("Attendance updated successfully");
        } else {
          toast.success("Attendance saved successfully");
        }
        await loadAttendance();
      } else {
        toast.error(res.message || "Failed to save attendance");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving attendance");
    }
  };

  const canOpenForm =
    selectedSession && selectedGrade && selectedSubject && date && !hasAttendance;

  const handleOpenForm = () => {
    if (!canOpenForm) return;
    setShowForm(true);
  };

  const handleFormSaved = async () => {
    setShowForm(false);
    await loadAttendance();
    // toast.success("Attendance saved successfully");
  };

  const handleCancel = () => {
    setSelectedSession(null);
    setSelectedGrade(null);
    setSelectedSubject(null);
    setStudents([]);
    setHasAttendance(false);
    toast.info("Selection cleared");
  };

  return (
    <div className="attendance-page p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="text-2xl font-semibold">Attendance</h2>
        <Button
          label="Mark Attendance"
          icon="pi pi-plus"
          className="p-button-raised p-button-primary"
          onClick={handleOpenForm}
          disabled={!canOpenForm}
        />
      </div>

      <Card className="mb-4 shadow-2 border-round-lg">
        <div className="grid p-fluid">
          <div className="col-12 md:col-6">
            <label>Session</label>
            <Dropdown
              options={sessions}
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.value)}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Session"
            />
          </div>

          <div className="col-12 md:col-6">
            <label>Grade</label>
            <Dropdown
              options={grades}
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.value)}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Grade"
            />
          </div>

          <div className="col-12 md:col-6">
            <label>Subject</label>
            <Dropdown
              options={subjects}
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.value)}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Subject"
            />
          </div>

          <div className="col-12 md:col-6">
            <label>Date</label>
            <Calendar
              value={date}
              onChange={(e) =>
                setDate(e.value ? new Date(e.value) : new Date())
              }
              dateFormat="yy-mm-dd"
              showIcon
              className="w-full"
            />
          </div>
        </div>
      </Card>

      <Card className="shadow-3 border-round-lg">
        <table className="attendance-table w-full">
          <thead>
            <tr>
              <th>Student Name</th>
              <th className="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-center p-4">
                  No students / no attendance data for selected filters.
                </td>
              </tr>
            ) : (
              students.map((s, idx) => (
                <tr key={s.id}>
                  <td>{s.full_name}</td>
                  <td className="text-center">
                    <div className="flex justify-content-center gap-3">
                      {["present", "absent", "leave", "late", "holiday"].map(
                        (st) => (
                          <label key={st} className="flex align-items-center gap-1">
                            <input
                              type="radio"
                              name={`status_${idx}`}
                              value={st}
                              checked={
                                String(s.status || "").toLowerCase() ===
                                st.toLowerCase()
                              }
                              onChange={() => handleStatusChange(s.id, st)}
                            />
                            <span>{st}</span>
                          </label>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="flex justify-content-end gap-3 mt-4">
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-danger"
            onClick={handleCancel}
          />
          <Button
            label={hasAttendance ? "Update Attendance" : "Save Attendance"}
            icon="pi pi-check"
            className="p-button-success"
            onClick={handleUpdateAttendance}
          />
        </div>
      </Card>

      {showForm && (
        <AttendanceForm
          sessionId={selectedSession}
          gradeId={selectedGrade}
          subjectId={selectedSubject}
          date={date}
          onClose={() => setShowForm(false)}
          onSaved={handleFormSaved}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default AttendancePage;
